import type { ReactNode } from "react"
import { IconCircleInfoOutline18 as InfoIcon } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"
import { Tooltip } from "@productlane/ui"

import { reportingCardClassName, reportingCardLoadingClassName } from "./theme"

interface ReportingChartFrameProps {
	title: string
	icon?: ReactNode
	description?: string
	legend?: ReactNode
	badge?: ReactNode
	isLoading?: boolean
	isEmpty?: boolean
	emptyMessage?: string
	className?: string
	// Drops the card chrome so the chart can sit flush against its neighbours.
	flush?: boolean
	children: ReactNode
}

export const ReportingChartFrame = ({
	title,
	icon,
	description,
	legend,
	badge,
	isLoading,
	isEmpty,
	emptyMessage = "No data in this range",
	className,
	flush,
	children,
}: ReportingChartFrameProps) => {
	if (isLoading) {
		return (
			<div
				className={cn(
					flush ? "animate-pulse" : reportingCardLoadingClassName,
					"p-5",
					className,
				)}
			>
				<div className="mb-4 h-4 w-40 rounded bg-gray-200 dark:bg-gray-700" />
				<div className="h-44 w-full rounded bg-gray-100/80 dark:bg-gray-800/50" />
			</div>
		)
	}

	return (
		<div
			className={cn(
				flush ? undefined : reportingCardClassName,
				"p-5",
				className,
			)}
		>
			<div className="mb-4 flex items-start justify-between gap-3">
				<div className="flex min-w-0 items-center gap-1.5">
					{icon ? (
						<span className="inline-flex shrink-0 text-tertiary">{icon}</span>
					) : null}
					<h3 className="truncate text-sm font-medium text-secondary">
						{title}
					</h3>
					{description ? (
						<Tooltip label={description}>
							<span className="inline-flex text-tertiary">
								<InfoIcon className="h-3.5 w-3.5" />
							</span>
						</Tooltip>
					) : null}
					{badge}
				</div>
				{legend ? <div className="shrink-0">{legend}</div> : null}
			</div>
			{isEmpty ? (
				<div className="flex h-44 items-center justify-center text-sm text-tertiary">
					{emptyMessage}
				</div>
			) : (
				children
			)}
		</div>
	)
}
